import { DateRangeComparisonOptions } from "./dateTimeUtils";
import { DayOfWeek, getFirstWeekDay } from "./culture";
import { DateTime } from "luxon";
import { DateOnly } from "./dateOnly";
import { TimeZone } from "./timezones";
import { isNil } from "client/Misc/utils";

export function getWeekNumber(
	date: DateOnly
): number {
	return DateTime.local(
		date.year,
		date.month,
		date.day
	)
		.localWeekNumber;
}

export function getDayOfWeek(
	date: DateOnly
): DayOfWeek {
	const dayNumber: number = new Date(date.year, date.monthIndex, date.day)
		.getDay();

	return dayNumber as DayOfWeek;
}

export function getStartOfWeek(date: DateOnly): DateOnly {
	const firstDayOfWeek: DayOfWeek = getFirstWeekDay();
	const currentDayOfWeek: DayOfWeek = getDayOfWeek(date);
	let shift: number = firstDayOfWeek - currentDayOfWeek;

	if (shift > 0) {
		shift -= 7;
	}

	return addDays(
		date,
		shift
	);
}

export function addDays(
	date: DateOnly,
	numberOfDays: number
): DateOnly {
	const dateTime: DateTime = DateTime.local(date.year, date.month, date.day)
		.plus({ days: numberOfDays });

	const result = new DateOnly(
		dateTime.year,
		dateTime.month - 1,
		dateTime.day
	);

	return result;
}

export function addMonths(
	date: DateOnly,
	numberOfMonths: number
): DateOnly {
	const luxonDate = DateTime.local(date.year, date.month, date.day)
		.plus({ months: numberOfMonths });

	const result = new DateOnly(
		luxonDate.year,
		luxonDate.month - 1,
		luxonDate.day
	);

	return result;
}

export function getNextHalfOrWholeHour(date: Date, timezone: TimeZone): Date {
	const newDate: DateTime = DateTime.fromJSDate(
		date,
		{
			zone: timezone.identifier
		}
	);

	if (newDate.minute < 30) {
		return newDate.set({
			minute: 30,
			second: 0,
			millisecond: 0
		})
			.toJSDate();
	} else {
		return newDate.plus({ hours: 1 })
			.set({
				minute: 0,
				second: 0,
				millisecond: 0
			})
			.toJSDate();
	}
}

export function addYears(
	date: DateOnly,
	numberOfYears: number
): DateOnly {
	const luxonDate = DateTime.local(date.year, date.month, date.day)
		.plus({ years: numberOfYears });

	const result = new DateOnly(
		luxonDate.year,
		luxonDate.month - 1,
		luxonDate.day
	);

	return result;
}

export function generateDateArray(from: DateOnly, amount: number): readonly DateOnly[] {
	const result: DateOnly[] = [];
	const isNegative: boolean = amount < 0;
	const amountToLoop: number = Math.abs(amount);

	for (let i = 0; i < amountToLoop; i++) {
		const entry: DateOnly = addDays(
			from,
			isNegative
				? -i
				: i
		);

		result.push(entry);
	}
	return result;
}

export function isBetween(
	date: DateOnly,
	start: DateOnly,
	end: DateOnly,
	options?: DateRangeComparisonOptions
) {
	const defaultOptions: DateRangeComparisonOptions = {
		shouldIncludeStart: true,
		shouldIncludeEnd: false
	};

	const {
		shouldIncludeStart,
		shouldIncludeEnd
	} = options ?? defaultOptions;

	const startToDateComparison: -1 | 0 | 1 = compareDates(start, date);
	const endToDateComparison: -1 | 0 | 1 = compareDates(end, date);

	const doesSatisfyStart: boolean = startToDateComparison === -1
		|| (shouldIncludeStart
			&& startToDateComparison === 0
		);

	const doesSatisfyEnd: boolean = endToDateComparison === 1
		|| (shouldIncludeEnd
			&& endToDateComparison === 0
		);

	return doesSatisfyStart && doesSatisfyEnd;
}

export function compareDates(date1: DateOnly, date2: DateOnly): -1 | 0 | 1 {
	if (date1.year < date2.year) {
		return -1;
	}

	if (date1.year > date2.year) {
		return 1;
	}

	if (date1.monthIndex < date2.monthIndex) {
		return -1;
	}

	if (date1.monthIndex > date2.monthIndex) {
		return 1;
	}

	if (date1.day < date2.day) {
		return -1;
	}

	if (date1.day > date2.day) {
		return 1;
	}

	return 0;
}

export function convertToDateTime(date: DateOnly, timezone: TimeZone): Date {
	const luxonDate: DateTime = DateTime.local(
		date.year,
		date.month,
		date.day,
		{ zone: timezone.identifier }
	);

	return luxonDate.toJSDate();
}

export function convertToDateOnly(dateTime: Date, timezone: TimeZone): DateOnly {
	const luxonDate: DateTime = DateTime.fromJSDate(dateTime, { zone: timezone.identifier });

	return new DateOnly(
		luxonDate.year,
		luxonDate.month - 1,
		luxonDate.day
	);
}

export function parseDtoDateOnlyIgnoringOffset(date: string): DateOnly | null;
export function parseDtoDateOnlyIgnoringOffset(date: string | nil): DateOnly | null;
export function parseDtoDateOnlyIgnoringOffset(date: string | nil): DateOnly | null {
	if (isNil(date)) {
		return null;
	}

	const valueWithPreservedTimeZone: DateTime = DateTime.fromISO(date, { setZone: true });

	if (!valueWithPreservedTimeZone.isValid) {
		return null;
	}

	return new DateOnly(
		valueWithPreservedTimeZone.year,
		valueWithPreservedTimeZone.month - 1,
		valueWithPreservedTimeZone.day
	);
}

export const daysInAWeek = 7;
export const monthsInAYear = 12;
export const yearsInADecade = 10;
export const monthsInADecade = monthsInAYear * yearsInADecade;